import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";

const ElectronicSignature2 = ({ onClose, onConfirm }) => {
  const [agreed, setAgreed] = useState(false);
  const [visible, setVisible] = useState(false);
  const [userData, setUserData] = useState(null);

  // 페이지 로드 시 모달이 하단에서 올라오도록 설정
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = Cookies.get(
          API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN,
        );

        if (!token) {
          throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
        }

        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/clients/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error("사용자 정보를 가져오는데 실패했습니다.");
        }

        const data = await response.json();
        setUserData(data);
      } catch (err) {
        console.error("사용자 정보 가져오기 실패:", err);
      } finally {
        setVisible(true);
      }
    };

    fetchUserData();
  }, []);

  // 모달 닫기 처리
  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  };

  // 동의 처리
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  const modalVariants = {
    hidden: {
      y: "100%",
      opacity: 1,
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300,
      },
    },
    exit: {
      y: "100%",
      opacity: 1,
      transition: {
        duration: 0.3,
      },
    },
  };

  return (
    <ModalOverlay onClick={handleClose}>
      <AnimatePresence>
        {visible && (
          <ModalContainer
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>소송위임장 / 담당변호사 지정서</ModalTitle>
              <CloseButton onClick={handleClose}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody>
              <MandateDocument>
                <MandateTitle>소송위임장 / 담당변호사 지정서</MandateTitle>

                <CaseInfo>
                  <CaseLine>
                    <CaseLabel>사 건</CaseLabel>
                    <CaseValue>:</CaseValue>
                    <CaseContent>손해배상(기)</CaseContent>
                  </CaseLine>
                  <CaseLine>
                    <CaseLabel>
                      원 고
                      <br />
                      (소송참여자)
                    </CaseLabel>
                    <CaseValue>:</CaseValue>
                    <CaseContent>{userData?.name || ""}</CaseContent>
                  </CaseLine>
                  <CaseLine>
                    <CaseLabel>피 고</CaseLabel>
                    <CaseValue>:</CaseValue>
                    <CaseContent>듀오정보주식회사</CaseContent>
                  </CaseLine>
                </CaseInfo>

                <IntroText>
                  위 사건에 관하여 원고는 아래 수임인에게 소송을 위임합니다.
                </IntroText>

                <Section>
                  <SectionNumber>1. 수임인 (법무법인)</SectionNumber>
                  <SectionContent>
                    <InfoLine>
                      <InfoLabel>명칭</InfoLabel>
                      <InfoValue>:</InfoValue>
                      <InfoContent>법무법인 더에이치 황해</InfoContent>
                    </InfoLine>
                    <InfoLine>
                      <InfoLabel>주소</InfoLabel>
                      <InfoValue>:</InfoValue>
                      <InfoContent>
                        인천광역시 미추홀구 학익소로 60, 제1층 (학익동,
                        반도빌딩)
                      </InfoContent>
                    </InfoLine>
                    <InfoLine>
                      <InfoLabel>연락처</InfoLabel>
                      <InfoValue>:</InfoValue>
                      <InfoContent>
                        (전화) 032-713-9667 / (전송) 032-251-1003
                      </InfoContent>
                    </InfoLine>
                  </SectionContent>
                </Section>

                <Section>
                  <SectionNumber>2. 수권 사항</SectionNumber>
                  <SectionContent>
                    위임인은 수임인에게 다음의 권한을 부여합니다.
                    <AuthorityList>
                      <AuthorityItem>
                        일체의 소송행위 및 부대행위 일체(개인정보보호법령상
                        분쟁조정절차 포함)
                      </AuthorityItem>
                      <AuthorityItem>
                        반소의 제기 및 응소, 상소의 제기 또는 취하
                      </AuthorityItem>
                      <AuthorityItem>
                        소의 취하, 재판상 및 재판외의 화해
                      </AuthorityItem>
                      <AuthorityItem>
                        복대리인 또는 공동대리인의 선임
                      </AuthorityItem>
                      <AuthorityItem>
                        판결원리금을 비롯한 목적물(보상금, 조정금, 화해금 등)의
                        수령
                      </AuthorityItem>
                      <AuthorityItem>
                        공탁물의 납부 및 회수, 출급, 공탁물 및 이자의 반환청구와
                        수령
                      </AuthorityItem>
                      <AuthorityItem>
                        담보권행사최고 및 담보취소신청, 동 신청에 의한 동의
                      </AuthorityItem>
                      <AuthorityItem>
                        담보취소결정 정본의 수령, 동 취소결정에 대한 항고권의
                        포기
                      </AuthorityItem>
                    </AuthorityList>
                  </SectionContent>
                </Section>

                <DateSection>
                  {(() => {
                    const now = new Date();
                    return `${now.getFullYear()}. ${
                      now.getMonth() + 1
                    }. ${now.getDate()}.`;
                  })()}
                </DateSection>

                <Section>
                  <SectionNumber>3. 위 임 인</SectionNumber>
                  <SectionContent>
                    <InfoLine>
                      <InfoLabel>성명</InfoLabel>
                      <InfoValue>:</InfoValue>
                      <InfoContent>
                        {userData?.name || ""} (인)
                      </InfoContent>
                    </InfoLine>
                    <InfoLine>
                      <InfoLabel>주소</InfoLabel>
                      <InfoValue>:</InfoValue>
                      <InfoContent>
                        {userData?.address || ""}
                        {userData?.addressDetail
                          ? ` ${userData.addressDetail}`
                          : ""}
                      </InfoContent>
                    </InfoLine>
                  </SectionContent>
                </Section>

                <Section>
                  <SectionNumber>4. 담당 변호사 지정</SectionNumber>
                  <SectionContent>
                    변호사법 제50조 제1항에 의하여 당 법인에서 위 업무를 담당할
                    변호사로 아래 변호사를 지정합니다.
                    <LawyerList>
                      천상현, 조준상, 이창선, 황규현, 김나형, 안호섭
                    </LawyerList>
                  </SectionContent>
                </Section>

                <SignatureSection>
                  <CourtText>서울중앙지방법원 귀중</CourtText>
                  <SealSection>
                    <SealImage src="/png/seal.png" alt="인장" />
                  </SealSection>
                </SignatureSection>

                <CheckboxContainer onClick={() => setAgreed(!agreed)}>
                  <CheckboxImage
                    src={
                      agreed ? "/png/checked.png" : "/png/checkedinActive.png"
                    }
                    alt="체크박스"
                  />
                  <CheckboxLabel>
                    위 내용을 모두 확인하고 동의합니다
                  </CheckboxLabel>
                </CheckboxContainer>
              </MandateDocument>
            </ModalBody>
            <ModalFooter>
              <ConfirmButton onClick={handleConfirm} disabled={!agreed}>
                동의하고 계속하기
              </ConfirmButton>
            </ModalFooter>
          </ModalContainer>
        )}
      </AnimatePresence>
    </ModalOverlay>
  );
};

// 스타일 컴포넌트
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 999;
  touch-action: none;

  @media (min-width: 769px) {
    align-items: center;
  }
`;

const ModalContainer = styled(motion.div)`
  width: 100%;
  max-width: 500px;
  background-color: white;
  border-radius: 16px 16px 0 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 80vh;

  @media (min-width: 769px) {
    border-radius: 16px;
    max-width: 420px;
    max-height: 90vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  flex: 1;
  text-align: center;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  padding: 0;
  margin: 0;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: 20px;
  overflow-y: auto;
  flex: 1;
  font-size: 14px;
  line-height: 1.6;
`;

const ModalFooter = styled.div`
  padding: 15px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: center;
`;

const ConfirmButton = styled.button`
  display: flex;
  width: 100%;
  height: 56px;
  padding: 0 14px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: ${(props) => (props.disabled ? "#E5E5E5" : "#E31C20")};
  color: ${(props) => (props.disabled ? "#999" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  border: none;
  opacity: ${(props) => (props.disabled ? 0.8 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 20px;
  cursor: pointer;
`;

const CheckboxImage = styled.img`
  width: 24px;
  height: 24px;
  margin-right: 10px;
`;

const CheckboxLabel = styled.div`
  font-size: 15px;
  font-weight: 500;
`;

// 위임장 스타일 컴포넌트
const MandateDocument = styled.div`
  font-family: "Noto Sans KR", sans-serif;
  color: #000;
  background: #fff;
  padding: 0;
  position: relative;
`;

const MandateTitle = styled.h1`
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 2px solid #000;
`;

const CaseInfo = styled.div`
  margin-bottom: 20px;
`;

const CaseLine = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  line-height: 1.8;
`;

const CaseLabel = styled.span`
  font-weight: 600;
  min-width: 95px;
  flex-shrink: 0;
`;

const CaseValue = styled.span`
  margin: 0 8px;
  flex-shrink: 0;
`;

const CaseContent = styled.span`
  flex: 1;
`;

const IntroText = styled.div`
  margin-bottom: 25px;
  line-height: 1.8;
  font-size: 14px;
`;

const Section = styled.div`
  margin-bottom: 25px;
`;

const SectionNumber = styled.div`
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 10px;
  color: #000;
`;

const SectionContent = styled.div`
  font-size: 14px;
  line-height: 1.8;
  color: #333;
  margin-left: 10px;
`;

const InfoLine = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 6px;
  line-height: 1.8;
`;

const InfoLabel = styled.span`
  font-weight: 500;
  min-width: 50px;
  flex-shrink: 0;
`;

const InfoValue = styled.span`
  margin: 0 8px;
  flex-shrink: 0;
`;

const InfoContent = styled.span`
  flex: 1;
`;

const AuthorityList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 10px 0;
  margin-left: 10px;
`;

const AuthorityItem = styled.li`
  margin-bottom: 6px;
  line-height: 1.8;
  position: relative;
  padding-left: 15px;

  &:before {
    content: "•";
    position: absolute;
    left: 0;
  }
`;

const LawyerList = styled.div`
  margin-top: 10px;
  line-height: 1.8;
`;

const DateSection = styled.div`
  text-align: right;
  margin: 30px 0 20px 0;
  font-weight: 500;
  font-size: 15px;
`;

const SignatureSection = styled.div`
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  line-height: 1.8;
  position: relative;
  min-height: 150px;
`;

const SignatureText = styled.div`
  font-size: 14px;
`;

const CourtText = styled.div`
  font-size: 14px;
  margin-top: 10px;
`;

const SealSection = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  width: 120px;
  height: 120px;
`;

const SealImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: contain;
`;

export default ElectronicSignature2;
